import { Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import { StudioComponentDefinition } from '../models/component-studio.models';

export interface ComponentStudioApiError {
  code: number;
  message: string;
  details?: string[];
}

interface ComponentStudioStateResponse {
  success: boolean;
  customDefinitions: StudioComponentDefinition[];
}

@Injectable({
  providedIn: 'root'
})
export class ComponentStudioApiService {
  private readonly API_URL = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getState(): Observable<StudioComponentDefinition[]> {
    return this.http
      .get<ComponentStudioStateResponse>(`${this.API_URL}/studio/components`)
      .pipe(
        map((response) => response.customDefinitions),
        catchError(this.handleError)
      );
  }

  saveState(customDefinitions: StudioComponentDefinition[]): Observable<StudioComponentDefinition[]> {
    return this.http
      .put<ComponentStudioStateResponse>(`${this.API_URL}/studio/components`, { customDefinitions })
      .pipe(
        map((response) => response.customDefinitions),
        catchError(this.handleError)
      );
  }

  saveStateKeepalive(customDefinitions: StudioComponentDefinition[]): void {
    if (typeof fetch === 'undefined') {
      return;
    }

    void fetch(`${this.API_URL}/studio/components`, {
      method: 'PUT',
      credentials: 'include',
      keepalive: true,
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ customDefinitions })
    }).catch(() => undefined);
  }

  private handleError = (error: HttpErrorResponse): Observable<never> => {
    let message = 'An unexpected error occurred';

    if (error.error?.error?.message) {
      message = error.error.error.message as string;
    } else if (error.error?.message) {
      message = error.error.message as string;
    } else if (error.message) {
      message = error.message;
    }

    return throwError(() => ({
      code: error.status,
      message,
      details: error.error?.error?.details as string[] | undefined
    } as ComponentStudioApiError));
  };
}
